/**
 * Detects typical AWS S3 XML error bodies (expired presigned URL, access denied).
 * Used when the app fetches a file URL; the browser may otherwise receive XML as a "file" or show it in a new tab.
 */
export const NOVA_FILE_UNAVAILABLE_MESSAGE =
  'This file is temporarily unavailable. Please reload the page and try again.';

export function isS3PresignedUrlErrorBody(body: string): boolean {
  const s = body.trim();
  if (s.length === 0) {
    return false;
  }
  const looksXml = s.startsWith('<?xml') || s.includes('<Error');
  if (!looksXml) {
    return false;
  }
  return (
    s.includes('AccessDenied') ||
    s.includes('Request has expired') ||
    s.includes('ExpiredToken') ||
    s.includes('SignatureDoesNotMatch')
  );
}

/**
 * Same-origin file URLs (e.g. API-proxied) may need cookies; cross-origin URLs
 * (e.g. S3 presigned) typically must not use credentials or the browser CORS check fails.
 */
export function shouldSendCredentialsForNovaFileUrl(url: string): boolean {
  if (typeof window === 'undefined' || !url) {
    return true;
  }
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.origin === window.location.origin;
  } catch {
    return true;
  }
}

/** Use for opening/downloading Nova attachment or saved-file URLs via fetch. */
export function fetchNovaFile(url: string): Promise<Response> {
  return fetch(url, {
    credentials: shouldSendCredentialsForNovaFileUrl(url) ? 'include' : 'omit'
  });
}
